var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { apiAuthInformation } from '@/actions/AuthActions';
import { apiGetCategory } from '@/actions/CategoryActions';
import { apiGetOneProfile } from '@/actions/ProfileActions';
import { propChildren } from '@/helpers/custom-prop-types';
import { useConstructor } from '@/helpers/use/useHook';
import { useUnmountRef } from '@/hooks/lifecycle/useUnmountRef';
import { PageViewEvent } from '@/pages/all/Event/PageViewEvent/PageViewEvent';
import { wrapDispatch } from '@/utils/actions/dispatch';
import { isLogin } from '@/utils/resources/auth/authResource';
import { singletonScriptState } from '@/utils/window/SingletonScriptState';
import initMartEvent from '../events';
import CustomRedirect from '../route/CustomRedirect';
import ErrorReporting from '../vendor/ErrorReporting';
import AppLoading from './AppLoading';
import CanonicalComponent from './Canonical/CanonicalComponent';
import JsonLdComponent from './JsonLd/JsonLdComponent';
const App = (props) => {
    const { children } = props;
    const { pathname } = useLocation();
    const dispatch = useDispatch();
    const unmountRef = useUnmountRef();
    const initialLoadedRef = useRef(false);
    const maintenance = useSelector((state) => state.maintenance.data || null);
    // 全ての初期値が読み込めている場合はAPI実行不要、一つでも読み込めていない場合は初期値取得用のAPIを実行させたい
    const [isInitialized, changeIsInitialized] = useState(singletonScriptState.isAllLoaded);
    useConstructor(() => {
        // エラー通知の設定
        ErrorReporting.setUp();
        // 各種イベントの初期化
        initMartEvent();
    });
    // 初期読み込みを実行（主にローカル実行用。初期値がScriptタグから取得できないので）
    useEffect(() => {
        if (initialLoadedRef.current) {
            return;
        }
        initialLoadedRef.current = true;
        /** 初期読み込みが必要なAPI一覧 */
        const promises = [];
        if (singletonScriptState.auth.isUndefined ||
            singletonScriptState.profile.isUndefined) {
            // 認証情報が初期取得できていなかった場合
            promises.push((() => __awaiter(void 0, void 0, void 0, function* () {
                let authPayload;
                if (singletonScriptState.auth.isUndefined) {
                    const response = yield wrapDispatch(dispatch, apiAuthInformation({}));
                    authPayload = {
                        payload: response.payload,
                    };
                }
                else {
                    authPayload = singletonScriptState.auth.value;
                }
                const isLoginUser = isLogin(authPayload);
                if (isLoginUser && singletonScriptState.profile.isUndefined) {
                    // ログイン状態、かつプロフィールが初期取得できていなかった場合
                    yield wrapDispatch(dispatch, apiGetOneProfile({}));
                }
            }))());
        }
        if (singletonScriptState.categories.isUndefined) {
            // カテゴリが初期取得できていなかった場合
            promises.push(wrapDispatch(dispatch, apiGetCategory({})));
        }
        // 未取得のAPIを実行
        ;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            yield Promise.all(promises);
            if (!unmountRef.current) {
                changeIsInitialized(true);
            }
        }))();
    }, [dispatch, unmountRef, initialLoadedRef]);
    if (isInitialized || pathname === '/maintenance') {
        return (_jsxs(_Fragment, { children: [_jsx(PageViewEvent, {}), _jsx(JsonLdComponent, { pathname: pathname }), _jsx(CanonicalComponent, { pathname: pathname }), _jsx("div", { className: 'portal-root' }), children] }));
    }
    if (maintenance) {
        return _jsx(CustomRedirect, { to: '/maintenance' });
    }
    return _jsx(AppLoading, {});
};
App.propTypes = {
    children: propChildren().isRequired,
};
export default App;
